var Pandora = Pandora || {};

// The Cmd+K palette. Every string it renders comes from the data-copy-* attributes the partial writes,
// so the locale files stay the single source of user-visible copy.
//
// Results are built with createElement and textContent rather than innerHTML: the titles and subtitles
// are landlord names, addresses and emails straight out of the database, and this widget renders them on
// every page of Pandora.
Pandora.GlobalSearchHandler = {
  ROOT_SELECTOR: '#global-search-palette',
  INPUT_SELECTOR: '#global-search-palette-input',
  RESULTS_SELECTOR: '#global-search-palette-results',
  SEE_ALL_SELECTOR: '.global-search-palette__see-all',
  OPEN_SELECTOR: '[data-global-search-open]',
  DISMISS_SELECTOR: '[data-global-search-dismiss]',
  ACTIVE_CLASS: 'global-search-palette__option--active',
  OPTION_ID_PREFIX: 'global-search-option-',

  // Long enough that typing a name does not fire a request per letter, short enough that the results
  // still feel attached to the keystroke.
  DEBOUNCE_MS: 250,

  init: function() {
    this._root = document.querySelector(this.ROOT_SELECTOR);
    if (!this._root) return;

    this._input = this._root.querySelector(this.INPUT_SELECTOR);
    this._results = this._root.querySelector(this.RESULTS_SELECTOR);
    this._seeAll = this._root.querySelector(this.SEE_ALL_SELECTOR);

    this._suggestionsUrl = this._root.dataset.suggestionsUrl;
    this._resultsUrl = this._root.dataset.resultsUrl;
    this._minimumLength = parseInt(this._root.dataset.minimumLength, 10) || 3;
    this._copy = {
      hint: this._root.dataset.copyHint,
      searching: this._root.dataset.copySearching,
      empty: this._root.dataset.copyEmpty,
      error: this._root.dataset.copyError,
      seeAll: this._root.dataset.copySeeAll
    };

    this._rows = [];
    this._activeIndex = -1;
    this._request = null;
    this._timer = null;

    this._bindShortcut();
    this._bindLauncher();
    this._bindDismiss();
    this._bindInput();
  },

  isOpen: function() {
    return !this._root.hasAttribute('hidden');
  },

  open: function() {
    if (this.isOpen()) return;

    this._previousFocus = document.activeElement;
    this._root.removeAttribute('hidden');
    document.body.classList.add('global-search-open');
    this._input.focus();
    this._input.select();

    // Reopening with a term still in the box should show its results again, not an empty panel.
    if (this._input.value) this._search();
  },

  close: function() {
    if (!this.isOpen()) return;

    this._cancelPending();
    this._root.setAttribute('hidden', '');
    document.body.classList.remove('global-search-open');
    if (this._previousFocus && this._previousFocus.focus) this._previousFocus.focus();
  },

  _bindShortcut: function() {
    document.addEventListener('keydown', function(event) {
      if (!event.key) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        this.isOpen() ? this.close() : this.open();
        return;
      }

      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close();
      }
    }.bind(this));
  },

  // Delegated from document so the launcher keeps working no matter where in the header it ends up.
  _bindLauncher: function() {
    document.addEventListener('click', function(event) {
      if (!event.target.closest(this.OPEN_SELECTOR)) return;

      event.preventDefault();
      this.open();
    }.bind(this));
  },

  _bindDismiss: function() {
    this._root.addEventListener('click', function(event) {
      if (event.target.closest(this.DISMISS_SELECTOR)) this.close();
    }.bind(this));
  },

  _bindInput: function() {
    this._input.addEventListener('input', function() {
      clearTimeout(this._timer);
      this._timer = setTimeout(this._search.bind(this), this.DEBOUNCE_MS);
    }.bind(this));

    this._input.addEventListener('keydown', function(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this._move(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this._move(-1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this._openActive();
      }
    }.bind(this));
  },

  // An operation number or public id is precise even at a single digit, so it skips the length floor
  // that stops a two-letter name from matching half the database.
  _isSearchable: function(term) {
    if (!term) return false;
    if (/^#?\d+$/.test(term)) return true;

    return term.length >= this._minimumLength;
  },

  _search: function() {
    var term = this._input.value.trim();

    this._cancelPending();

    if (!this._isSearchable(term)) {
      this._renderMessage(this._copy.hint);
      this._hideSeeAll();
      return;
    }

    this._renderMessage(this._copy.searching);

    // Every keystroke supersedes the one before it, so the in-flight request is dropped rather than
    // left to arrive out of order and overwrite fresher results.
    this._request = new AbortController();

    fetch(this._suggestionsUrl + '?term=' + encodeURIComponent(term), {
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      credentials: 'same-origin',
      signal: this._request.signal
    })
      .then(function(response) {
        if (!response.ok) throw new Error('Global search responded ' + response.status);
        return response.json();
      })
      .then(this._render.bind(this))
      .catch(function(error) {
        if (error.name === 'AbortError') return;
        this._renderMessage(this._copy.error);
        this._hideSeeAll();
      }.bind(this));
  },

  _cancelPending: function() {
    clearTimeout(this._timer);
    if (this._request) this._request.abort();
    this._request = null;
  },

  _render: function(payload) {
    if (!this.isOpen()) return;

    this._results.textContent = '';
    this._rows = [];
    this._activeIndex = -1;

    if (!payload.groups.length) {
      this._renderMessage(this._copy.empty.replace('{term}', payload.term));
      this._hideSeeAll();
      return;
    }

    payload.groups.forEach(this._renderGroup.bind(this));
    this._input.setAttribute('aria-expanded', 'true');
    this._showSeeAll(payload);
    this._setActive(0);
  },

  _renderGroup: function(group) {
    var section = document.createElement('div');
    section.className = 'global-search-palette__group';

    var heading = document.createElement('div');
    heading.className = 'global-search-palette__group-label';

    var icon = document.createElement('i');
    icon.className = group.icon;
    heading.appendChild(icon);
    heading.appendChild(document.createTextNode(' ' + group.label));
    section.appendChild(heading);

    group.results.forEach(function(result) {
      section.appendChild(this._buildOption(result));
    }.bind(this));

    this._results.appendChild(section);
  },

  _buildOption: function(result) {
    var option = document.createElement('div');
    option.className = 'global-search-palette__option';
    option.setAttribute('role', 'option');
    option.id = this.OPTION_ID_PREFIX + this._rows.length;

    var title = document.createElement('span');
    title.className = 'global-search-palette__option-title';
    title.textContent = result.title;
    option.appendChild(title);

    if (result.subtitle) {
      var subtitle = document.createElement('span');
      subtitle.className = 'global-search-palette__option-subtitle';
      subtitle.textContent = result.subtitle;
      option.appendChild(subtitle);
    }

    // Why this row came back. Without it a rent matched through its broker looks identical to one
    // matched through its tenant, and the row shows neither.
    if (result.matched) {
      var matched = document.createElement('span');
      matched.className = 'global-search-palette__option-matched';
      matched.textContent = result.matched;
      option.appendChild(matched);
    }

    var index = this._rows.length;
    option.addEventListener('click', function() {
      window.location.href = result.url;
    });
    option.addEventListener('mousemove', function() {
      this._setActive(index);
    }.bind(this));

    this._rows.push({ element: option, url: result.url });

    return option;
  },

  _renderMessage: function(message) {
    this._results.textContent = '';
    this._rows = [];
    this._activeIndex = -1;
    this._input.setAttribute('aria-expanded', 'false');
    this._input.removeAttribute('aria-activedescendant');

    var element = document.createElement('div');
    element.className = 'global-search-palette__message';
    element.textContent = message;
    this._results.appendChild(element);
  },

  _showSeeAll: function(payload) {
    this._seeAll.textContent = this._copy.seeAll.replace('{count}', payload.total_count);
    this._seeAll.href = this._resultsUrl + '?term=' + encodeURIComponent(payload.term);
    this._seeAll.removeAttribute('hidden');
  },

  _hideSeeAll: function() {
    this._seeAll.setAttribute('hidden', '');
  },

  _move: function(delta) {
    if (!this._rows.length) return;

    // Wraps, so holding one arrow key cycles instead of dead-ending at either edge.
    var next = (this._activeIndex + delta + this._rows.length) % this._rows.length;
    this._setActive(next);
  },

  _setActive: function(index) {
    if (!this._rows.length) return;

    if (this._activeIndex >= 0 && this._rows[this._activeIndex]) {
      this._rows[this._activeIndex].element.classList.remove(this.ACTIVE_CLASS);
      this._rows[this._activeIndex].element.setAttribute('aria-selected', 'false');
    }

    this._activeIndex = index;
    var active = this._rows[index].element;
    active.classList.add(this.ACTIVE_CLASS);
    active.setAttribute('aria-selected', 'true');
    active.scrollIntoView({ block: 'nearest' });
    this._input.setAttribute('aria-activedescendant', active.id);
  },

  // Enter on a highlighted row opens it; Enter with nothing highlighted falls through to the full
  // results page, so the key always does something.
  _openActive: function() {
    var active = this._rows[this._activeIndex];
    if (active) {
      window.location.href = active.url;
      return;
    }

    var term = this._input.value.trim();
    if (term) window.location.href = this._resultsUrl + '?term=' + encodeURIComponent(term);
  }
};

document.addEventListener('DOMContentLoaded', function() {
  Pandora.GlobalSearchHandler.init();
});
